import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PlusAuthenticationService } from '@karve.it/core';
import { SelectItem } from 'primeng/api';
import { Observable, Subscription, filter, first } from 'rxjs';
import { SubSink } from 'subsink';

import { ColorSchemeService } from '../color-scheme.service';
import { dayjs } from '../core/dayjs/dayjs';
import { AdvancedPeriodOptions, advancedPeriodOptions } from '../reports/reports.constants';
import { QueryParamsService } from '../shared/query-params.service';

import { ComparedTo, DashboardStore, comparedToLsKey, comparedToValues, decodeUser, groupLeadsByValues, jobStages, pipelineMetrics, pipelinePeriods } from './dashboardv2.store';

type ValidationEntry<Validated> = {
  allowed: ((text: string) => Validated) | readonly Validated[];
  setValue: (value: Validated | Observable<Validated>) => Subscription;
};

// Regex to validate the format MMMM-YYYY (case-insensitive)
const MONTH_REGEX = /^(january|february|march|april|may|june|july|august|september|october|november|december)-\d{4}$/i;

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-dashboardv2',
  templateUrl: './dashboardv2.component.html',
  styleUrls: [
    './dashboardv2.component.scss',
    '../jobsv2/colors.scss',
  ],
  providers: [DashboardStore],
})
export class Dashboardv2Component implements OnInit, OnDestroy {

  tabs = [
    {
      name: 'Revenue',
      slug: 'revenue',
    },
    {
      name: 'Engine',
      slug: 'engine',
    },
  ];

  periodOptions = [
    'Month of Year',
    ...advancedPeriodOptions,
  ];

  currencyOptions = [
    'USD',
    'CAD',
    'All',
  ] as const;

  comparedToOptions: SelectItem<ComparedTo>[] = comparedToValues
    .map((val) => ({
      label: `Compared to ${val}`,
      value: val,
    }));


  month: string;

  customPeriod: Date[];

  lastValidCustomPeriod: Date[];

  setPeriod = this.dashboardStore.setPeriod;

  setMonth = this.dashboardStore.setMonth;

  setComparedTo = this.dashboardStore.setComparedTo;

  setCurrency = this.dashboardStore.setCurrency;

  settings$ = this.dashboardStore.settingsViewModel$;

  subs = new SubSink();

  user = this.plusAuth.user;

  constructor(
    private dashboardStore: DashboardStore,
    private route: ActivatedRoute,
    private router: Router,
    private queryParamsService: QueryParamsService,
    public colorScheme: ColorSchemeService,
    private plusAuth: PlusAuthenticationService,
  ){}

  ngOnInit(): void {
    this.watchSettings();
    this.readUrlParams();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  watchSettings() {
    this.subs.sink = this.settings$.subscribe((settings) => {
      this.month = settings.month;
      this.customPeriod = settings.customPeriod;
      this.lastValidCustomPeriod = settings.customPeriod;
    });
  }

  readUrlParams() {
    this.subs.sink = this.route.queryParams
      .pipe(
        filter(params => 'zone' in params),
        first(),
      )
      .subscribe((params) => {

        const copy = { ...params };

        const validPeriod = this.isValidPeriod(params.period);

        const keys = [
          'currency',
          'customPeriod',
          'month',
          'comparedTo',
          'pipelinePeriod',
          'metric',
          'groupLeadsBy',
          'jobStage',
          'status',
          'estimator',
          'salesAgent',
        ] as const;

        const validationMap: Partial<Record<(typeof keys)[number], ValidationEntry<any>>> = {
          currency: {
            allowed: this.currencyOptions,
            setValue: this.setCurrency,
          },
          customPeriod: {
            allowed: (text: string) => this.queryParamsService.parseStartEndQueryParam(text),
            setValue: this.dashboardStore.setCustomPeriod,
          },
          month: {
            allowed: this.isValidMonth,
            setValue: this.setMonth,
          },
          comparedTo: {
            setValue: this.setComparedTo,
            allowed: this.isValidComparedTo,
          },
          groupLeadsBy: {
            setValue: this.dashboardStore.setGroupLeadsBy,
            allowed: groupLeadsByValues,
          },
          metric: {
            setValue: this.dashboardStore.setPipelineMetric,
            allowed: pipelineMetrics,
          },
          pipelinePeriod: {
            setValue: this.dashboardStore.setPipelinePeriod,
            allowed: pipelinePeriods,
          },
          jobStage: {
            setValue: this.dashboardStore.setJobStage,
            allowed: jobStages,
          },
          status: {
            setValue: this.dashboardStore.setStatus,
            allowed: [ 'open', 'closed' ] as const,
          },
          estimator: {
            setValue: this.dashboardStore.setEstimator,
            allowed: this.isValidUser,
          },
          salesAgent: {
            setValue: this.dashboardStore.setSalesAgent,
            allowed: this.isValidUser,
          }
        };

        if (!validPeriod) {
          this.clearParams();
          this.checkLocalStorage();
          return;
        }

        this.setPeriod(validPeriod);

        let hasInvalidParams = false;

        const validate = (key: typeof keys[number]) => {

          if (!validationMap[key]) {
            throw new Error(`Invalid key ${key} for validation map`);
          }

          const isValid = this.checkParam({
            copy,
            params,
            key,
            validation: validationMap[key],
          });

          if (!isValid) {
            hasInvalidParams = true;
          }
        }

        validate('currency');
        validate('groupLeadsBy');
        validate('metric');
        validate('pipelinePeriod');
        validate('jobStage');
        validate('status');
        validate('estimator');
        validate('salesAgent');

        if (validPeriod === 'Custom') {
          validate('customPeriod');
        } else if (params.customPeriod) {
          copy.customPeriod = null;
          hasInvalidParams = true;
        }

        if (validPeriod === 'Month of Year') {
          validate('month');
        } else if (params.month) {
          copy.month = null;
          hasInvalidParams = true;
        }

        if (validPeriod === 'Month of Year' || validPeriod === 'MTD') {
          validate('comparedTo');
        } else if (params.comparedTo) {
          copy.comparedTo = null;
          hasInvalidParams = true;
        }

        if ((validPeriod === 'Month of Year' || validPeriod === 'MTD') && copy.comparedTo === null) {
          this.checkLocalStorage();
        }

        if (typeof copy.source === 'string') {
          this.dashboardStore.setSource(copy.source.replaceAll('-', ' '));
        }

        if (typeof copy.jobOrigin === 'string') {
          this.dashboardStore.setJobOrigin(copy.jobOrigin.replaceAll('-', ' '));
        }

        if (typeof copy.estimateMethod === 'string') {
          this.dashboardStore.setEstimateMethod(copy.estimateMethod.replaceAll('-', ' '));
        }

        if (typeof copy.attentionExplanation === 'string') {
          this.dashboardStore.setAttentionExplanation(copy.attentionExplanation);
        }

        if (typeof copy.jobClosureReason === 'string') {
          this.dashboardStore.setJobClosureReason(copy.jobClosureReason);
        }

        if (typeof copy.utmSource === 'string') {
          this.dashboardStore.setUtmSource(copy.utmSource.replaceAll('-', ' '));
        }

        if (typeof copy.utmMedium === 'string') {
          this.dashboardStore.setUtmMedium(copy.utmMedium.replaceAll('-', ' '));
        }

        if (typeof copy.utmCampaign === 'string') {
          this.dashboardStore.setUtmCampaign(copy.utmCampaign.replaceAll('-', ' '));
        }

        if (typeof copy.utmContent === 'string') {
          this.dashboardStore.setUtmContent(copy.utmContent.replaceAll('-', ' '));
        }

        if (typeof copy.utmTerm === 'string') {
          this.dashboardStore.setUtmTerm(copy.utmTerm.replaceAll('-', ' '));
        }

        if (hasInvalidParams) {
          this.router.navigate([], {
            queryParams: copy,
            queryParamsHandling: 'merge',
          });
        }

        // function validate(key: typeof keys[number]) {

      });
  }

  isValidPeriod(text: string) {
    if (!text) { return };
    const decoded = text.replaceAll('-', ' ');
    for (const val of [ ...advancedPeriodOptions, 'Month of Year' ]) {
      if (val === decoded) {
        return decoded as (AdvancedPeriodOptions | 'Month of Year');
      }
    }
  }

  isValidMonth(text: string): string | null {
    if (!text || typeof text !== 'string') return null;

    // Validate the format
    if (!MONTH_REGEX.test(text)) return null;

    // Replace hyphens with spaces and trim extra whitespace
    const normalizedText = text.replace(/-/g, ' ').trim();
    const parsedDate = dayjs(normalizedText, 'MMMM YYYY', true);

    return parsedDate.isValid() ? parsedDate.format('MMMM YYYY') : null;
  }

  isValidComparedTo(text: string) {
    if (!text) { return };
    const decoded = text.replaceAll('-', ' ');
    for (const val of comparedToValues) {
      if (val === decoded) {
        return decoded;
      }
    }
  }

  isValidUser(text: string) {
    if (!text) { return };

    if (text === 'None') {
      return text;
    }

    if (text.split('.').length !== 3) { return; }

    const user = decodeUser(text);

    if (!UUID_REGEX.test(user.id)) { return; }

    return text;
  }

  checkLocalStorage() {
    const validComparedToFromLocalStorage = this.isValidComparedTo(localStorage.getItem(comparedToLsKey));

    if (validComparedToFromLocalStorage) {
      this.setComparedTo(validComparedToFromLocalStorage);
    }
  }

  clearParams() {
    return this.router.navigate([], {
      queryParams: {
        period: null,
        customPeriod: null,
        month: null,
        comparedTo: null,
      },
      queryParamsHandling: 'merge',
    });
  }

  checkParam<K>(input: {
    params: Params;
    key: string;
    copy: { [x: string]: any };
    validation: ValidationEntry<K>;
  }): boolean {

    const {
      params,
      key,
      copy,
      validation: {
        allowed,
        setValue,
      }
    } = input;

    let getValidValue: (text: string) => K;

    if (!isArray(allowed)) {
      getValidValue = allowed;
    } else {

      getValidValue = (getValidValueInput: string) => {

        let value = getValidValueInput;

        if (typeof value === 'string') {
          value = value.replaceAll('-', ' ');
        }

        if (!isInArray(allowed, value)) { return; }

        return value;
      }
    }

    const validValue = getValidValue(params[key]);

    if (validValue) {
      setValue(validValue);
      return true;
    }

    copy[key] = null;
    return false;
  }

  validateCustomPeriod() {
    if (this.customPeriod.filter(Boolean).length === 2) {
      this.dashboardStore.setCustomPeriod(this.customPeriod);
    } else {
      this.customPeriod = this.lastValidCustomPeriod;
    }
  }
}

function isArray<T>(value: readonly T[] | ((text: string) => T | undefined)): value is readonly T[] {
  return Array.isArray(value);
}

function isInArray<T>(array: readonly T[], value: unknown): value is T {
  return array.includes(value as T);
}
